import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useMarket } from './contexts/MarketContext';
import { Toolbar } from './components/Toolbar';
import { Canvas } from './components/Canvas';
import { PropertiesPanel } from './components/PropertiesPanel';
import { MapIconEditor } from './components/MapIconEditor';
import { CategoryNavigation, Category } from './components/CategoryNavigation';
import { SettingsPanel, renderCategoryContent } from './components/SettingsPanel';
import { MobileBottomNav } from './components/MobileBottomNav';
import { MobileBottomSheet } from './components/MobileBottomSheet';
import { ECommerceCard } from './components/ECommerceCard';
import { AnnouncementBar } from './components/AnnouncementBar';
import { PricingSection } from './components/PricingSection';
import { FeaturesSection } from './components/FeaturesSection';
import { TestimonialsSection } from './components/TestimonialsSection';
import { FAQSection } from './components/FAQSection';
import { Footer } from './components/Footer';
import { LegalPage } from './components/LegalPage';
import { SEOMeta } from './components/SEOMeta';
import { JsonLd } from './components/JsonLd';
import { ErrorBoundary } from './components/ErrorBoundary';
import { OpeningSoonOverlay } from './components/OpeningSoonOverlay';
// import { CheckoutFooter } from './components/CheckoutFooter';
import {
  CanvasElement,
  MapIcon,
  MapSettings,
  PosterSettings,
  POSTER_SIZES,
  WorkspaceBackground,
  PosterTextData,
  MapStylePreset,
  LayerColors,
} from './types/canvas';
import type { MapConfig } from './types';
import { DEFAULT_POSTER_STYLE_ID, getPosterStyleDefinition } from './config/posterStyles';
import { getMapIcon } from './lib/api';
import { normalizeHexColor, validateHexColor } from './utils/iconUtils';
import { useIsMobile } from './utils/deviceDetection';
import { initPixel, trackPixelEvent } from './lib/pixel';
import { formatCoordinates } from './utils/coordinates';

// Mobile checkout button component - extrahováno z IIFE pro lepší performance
function MobileCheckoutButton({
  posterSettings,
  checkoutLoading,
  onCheckout,
  t
}: {
  posterSettings: PosterSettings;
  checkoutLoading: boolean;
  onCheckout: (price?: number) => void;
  t: (key: string, options?: any) => string;
}) {
  const [prices, setPrices] = useState({ poster: 39.90, hanger: 14.90, frame: 39.90 });

  useEffect(() => {
    async function loadPrices() {
      try {
        const { getAllVariants } = await import('./lib/variants');
        // Use selected size (important for A2)
        const currentSize = posterSettings.size || 'B2';
        const variants = await getAllVariants({ size: currentSize, onlyAvailable: false });

        const posterVariant = variants.find(v => v.frame_type === 'none');
        const frameVariant = variants.find(v => v.frame_type === 'frame' && v.frame_color === 'pine');
        const hangerVariant = variants.find(v => v.frame_type === 'hanger' && v.frame_color === 'pine');

        // Fallback prices based on size (same as ECommerceCard)
        const fallbackPosterPrice = currentSize === 'A2' ? 29.90 : 39.90;
        const fallbackFramePrice = currentSize === 'A2' ? 39.90 : 39.90;
        const fallbackHangerPrice = currentSize === 'A2' ? 14.90 : 14.90;

        setPrices({
          poster: posterVariant?.price_cents ? posterVariant.price_cents / 100 : fallbackPosterPrice,
          frame: frameVariant?.price_cents ? frameVariant.price_cents / 100 : fallbackFramePrice,
          hanger: hangerVariant?.price_cents ? hangerVariant.price_cents / 100 : fallbackHangerPrice,
        });
      } catch (error) {
        console.error('Error loading prices:', error);
      }
    }
    loadPrices();
  }, [posterSettings.size]); // Re-load when size changes

  // Výpočet ceny na základě výběru hangeru (stejná logika jako v ECommerceCard)
  const discountedPrice = useMemo(() => {
    let price = prices.poster; // Základní cena printu
    if (posterSettings.frame === 'hanger') {
      price += prices.hanger; // Přidat cenu hangeru
    } else if (posterSettings.frame === 'frame') {
      price += prices.frame; // Přidat cenu rámu
    }
    return price;
  }, [posterSettings.frame, prices]);

  const basePrice = useMemo(() => {
    // 30% sleva: discountedPrice = basePrice * 0.7
    return discountedPrice / 0.7;
  }, [discountedPrice]);

  return (
    <div className="fixed bottom-16 left-0 right-0 z-20 px-0 safe-area-inset-bottom">
      <div className="w-full p-4">
        <button
          onClick={() => onCheckout(discountedPrice)}
          disabled={checkoutLoading}
          className={`w-full flex items-center justify-center gap-3 py-4 text-base font-bold transition-all duration-200 rounded-2xl ${checkoutLoading
            ? 'bg-charcoal-medium text-beige-light cursor-not-allowed'
            : 'bg-green-dark hover:bg-green-forest text-white-pure active:bg-green-forest'
            }`}
        >
          {checkoutLoading ? (
            <div className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
              <span>{t('ecommerce.processing', { defaultValue: 'Zpracovávám...' })}</span>
            </div>
          ) : (
            <>
              <span>{t('ecommerce.addToCart')}</span>
              <span className="flex items-baseline gap-2">
                <span className="text-sm line-through opacity-70">{basePrice.toFixed(2)}€</span>
                <span>{discountedPrice.toFixed(2)}€</span>
              </span>
            </>
          )}
        </button>
      </div>
    </div>
  );
}

function App() {
  const { t, i18n } = useTranslation();
  useMarket(); // ⭐ Market configuration (prepared for future use)
  const [elements, setElements] = useState<CanvasElement[]>([]); // Pouze ikony (legacy, pro kompatibilitu)
  const [mapIcons, setMapIcons] = useState<MapIcon[]>([]); // Nové SVG ikony na geografických souřadnicích
  const [selectedIconId, setSelectedIconId] = useState<string | null>(null); // ID vybrané ikony z IconSelector
  const [selectedMapIconId, setSelectedMapIconId] = useState<string | null>(null); // ID vybrané ikony na mapě pro editaci
  const [selectedIconColor, setSelectedIconColor] = useState<string>('#000000'); // Barva pro nově vkládané ikony
  const selectedIconIdRef = useRef<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [nextZIndex, setNextZIndex] = useState(1);

  // Update ref when selectedIconId changes
  useEffect(() => {
    selectedIconIdRef.current = selectedIconId;
  }, [selectedIconId]);

  // Mobile detection - musí být před inicializací mapSettings, protože se používá v initial state
  const isMobile = useIsMobile();

  // Initialize Meta Pixel
  useEffect(() => {
    initPixel();

    // Update HTML lang attribute
    document.documentElement.lang = i18n.language;

    // Debug i18n

  }, [i18n.language]);



  const [workspaceBackground, setWorkspaceBackground] = useState<WorkspaceBackground>('gray');
  const [selectedCategory, setSelectedCategory] = useState<Category>('map');

  // Text data pro statické textové elementy
  const [textData, setTextData] = useState<PosterTextData>({
    title: 'Helsinki',
    subtitle: t('countries.Finland'),
    detail: '60.1699°N / 24.9384°E'
  });

  const [mapSettings, setMapSettings] = useState<MapSettings>({
    centerLat: 60.1699, // Helsinki, Finland
    centerLng: 24.9384,
    zoom: 11.7,
    colorScheme: 'monochrome',
    mapLayer: 'no-labels', // Bez popisků defaultně
    showLabels: false, // Skryté popisky defaultně
    showBuildings: true,
    customColors: undefined, // @deprecated - používejte colors a colorPresetId
    selectedStyleId: DEFAULT_POSTER_STYLE_ID,
    detailLevel: 'standard', // Standardní úroveň detailů
    showMinorRoads: true, // Zobrazit vedlejší silnice
    showPaths: true, // Zobrazit chodníky a stezky
    showPedestrian: true, // Zobrazit pěší zóny
    mapDetailMultiplier: isMobile ? 2 : 4, // Dynamicky podle zařízení: 2x pro mobil, 4x pro desktop
  });

  // Flag pro sledování, zda byl draft načten a zda obsahuje barvy
  const draftLoadedRef = useRef(false);
  const draftHasColorsRef = useRef(false);

  // Pomocná funkce pro načtení "simple blue" presetu
  const loadSimpleBluePreset = async () => {
    const { getColorPresetByScheme, getColorPresets } = await import('./lib/api');

    // Zkusit najít preset "simple blue" podle tagu/názvu
    let presetResult = await getColorPresetByScheme('simple blue');

    // Pokud se nenašel podle tagu, zkusit najít podle názvu v seznamu všech presetů
    if (!presetResult.success || !presetResult.data) {
      const allPresetsResult = await getColorPresets(true);
      if (allPresetsResult.success && allPresetsResult.data) {
        const simpleBluePreset = allPresetsResult.data.find(
          (p: any) => p.name.toLowerCase() === 'simple blue'
        );
        if (simpleBluePreset) {
          presetResult = { success: true, data: simpleBluePreset };
        }
      }
    }

    return presetResult;
  };

  // Načíst barvy z Supabase při inicializaci a při změně colorScheme
  useEffect(() => {
    let cancelled = false;

    const loadColorsFromSupabase = async () => {
      // Pokud už máme colors nebo colorPresetId, nenačítat znovu
      if (mapSettings.colors || mapSettings.colorPresetId) {
        return;
      }

      // Pokud ještě nebyl načten draft, počkat (draft se načítá asynchronně)
      if (!draftLoadedRef.current) {
        // Počkat, aby se draft mohl načíst
        await new Promise(resolve => setTimeout(resolve, 300));
        // Pokud draft obsahuje barvy, neukládat znovu
        if (cancelled || draftHasColorsRef.current) {
          return;
        }
      }

      // Pokud draft obsahuje barvy, neukládat znovu
      if (draftHasColorsRef.current) {
        return;
      }

      try {
        // Načíst preset "simple blue" jako výchozí
        const presetResult = await loadSimpleBluePreset();

        if (cancelled) return;

        // Pokud se našel preset, použít jeho barvy a ID
        if (presetResult.success && presetResult.data) {
          const colors = presetResult.data.colors;
          const colorPresetId = presetResult.data.id;

          setMapSettings(prev => {
            // Pokud už máme barvy (z draftu), neukládat znovu
            if (prev.colors || prev.colorPresetId) {
              return prev;
            }
            return {
              ...prev,
              colors,
              colorPresetId,
            };
          });
        } else {
          // Fallback na původní logiku podle colorScheme
          const { getColorsFromSettingsAsync } = await import('./config/mapboxStyles');
          const colors = await getColorsFromSettingsAsync(mapSettings);

          if (cancelled) return;

          const { getColorPresetByScheme } = await import('./lib/api');
          const schemePresetResult = await getColorPresetByScheme(mapSettings.colorScheme);

          if (cancelled) return;

          setMapSettings(prev => {
            // Pokud už máme barvy (z draftu), neukládat znovu
            if (prev.colors || prev.colorPresetId) {
              return prev;
            }
            return {
              ...prev,
              colors,
              colorPresetId: schemePresetResult.success && schemePresetResult.data ? schemePresetResult.data.id : undefined,
            };
          });
        }
      } catch (error) {
        console.error('Failed to load colors from Supabase:', error);
      }
    };

    loadColorsFromSupabase();

    return () => {
      cancelled = true;
    };
  }, [mapSettings.colorScheme]); // Načíst znovu pouze při změně colorScheme

  const [posterSettings, setPosterSettings] = useState<PosterSettings>({
    size: 'A2',
    orientation: 'portrait',
    frame: 'none',
    frameColor: 'pine',
    styleId: DEFAULT_POSTER_STYLE_ID,
  });

  // Canvas dimensions state - now fixed to B2 size for consistent visual preview
  // The actual selected size (A2/B2/etc) is used only for checkout/pricing logic
  const [canvasDimensions, setCanvasDimensions] = useState(() => {
    // Always use B2 dimensions for visual rendering
    const size = POSTER_SIZES['B2'];
    return posterSettings.orientation === 'portrait'
      ? { width: size.width, height: size.height }
      : { width: size.height, height: size.width };
  });

  // Update canvas dimensions ONLY when orientation changes
  // We ignore size changes for visual rendering as requested by user
  useEffect(() => {
    // Always use B2 dimensions for visual rendering
    const size = POSTER_SIZES['B2'];
    setCanvasDimensions(
      posterSettings.orientation === 'portrait'
        ? { width: size.width, height: size.height }
        : { width: size.height, height: size.width }
    );
  }, [posterSettings.orientation]); // Removed posterSettings.size dependency

  // Track title from text data for e-commerce
  const [posterTitle, setPosterTitle] = useState('Helsinki');

  // Checkout loading state
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  // Opening soon overlay state
  const [showOpeningSoonOverlay, setShowOpeningSoonOverlay] = useState(false);

  // Legal page state
  const [legalPage, setLegalPage] = useState<string | null>(null);

  const headerRef = useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState(100);

  // Dynamický výpočet výšky headeru
  useEffect(() => {
    const updateHeaderHeight = () => {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.offsetHeight);
      }
    };

    updateHeaderHeight();
    window.addEventListener('resize', updateHeaderHeight);
    return () => window.removeEventListener('resize', updateHeaderHeight);
  }, []);

  // Mobile bottom sheet state
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const [isIconEditorBottomSheetOpen, setIsIconEditorBottomSheetOpen] = useState(false);

  // Handle category select on mobile - open bottom sheet
  const handleMobileCategorySelect = useCallback((category: Category) => {
    setSelectedCategory(category);
    // Always open bottom sheet when category is selected on mobile
    setIsBottomSheetOpen(true);
  }, []);

  // Handle icon select - close bottom sheet on mobile when icon is selected
  const handleIconSelect = useCallback((iconId: string | null) => {
    setSelectedIconId(iconId);
    // Close bottom sheet on mobile when icon is selected for insertion
    if (isMobile && iconId) {
      setIsBottomSheetOpen(false);
    }
  }, [isMobile]);

  // Open bottom sheet when category changes on mobile (if already open)
  useEffect(() => {
    if (isMobile && isBottomSheetOpen) {
      // Sheet is already open, just ensure it stays open
      // This handles the case when user switches categories while sheet is open
    }
  }, [selectedCategory, isMobile, isBottomSheetOpen]);

  // Get category title for bottom sheet
  const getCategoryTitle = (category: Category): string => {
    const titles: Record<Category, string> = {
      map: 'Map',
      style: 'Style',
      text: 'Text',
      'frames-and-size': 'Frames & Size',
      icons: 'Icons',
    };
    return titles[category];
  };

  const posterStyleId = posterSettings.styleId || mapSettings.selectedStyleId || DEFAULT_POSTER_STYLE_ID;
  const posterStyleDefinition = useMemo(() => getPosterStyleDefinition(posterStyleId), [posterStyleId]);


  // Handler pro výběr stylu
  const handleStyleSelect = async (preset: MapStylePreset) => {
    // Načíst barvy z Supabase pro nový colorScheme
    let colors: LayerColors | undefined;
    let colorPresetId: string | undefined;

    try {
      const { getColorsFromSettingsAsync } = await import('./config/mapboxStyles');
      const tempSettings = { ...mapSettings, colorScheme: preset.colorScheme };
      colors = await getColorsFromSettingsAsync(tempSettings);

      // Najít preset ID podle colorScheme
      const { getColorPresetByScheme } = await import('./lib/api');
      const presetResult = await getColorPresetByScheme(preset.colorScheme);
      colorPresetId = presetResult.success && presetResult.data ? presetResult.data.id : undefined;
    } catch (error) {
      console.error('Failed to load colors from Supabase for style:', error);
    }

    setMapSettings(prev => ({
      ...prev, // zachovat všechna existující nastavení
      colorScheme: preset.colorScheme,
      mapLayer: preset.mapLayer,
      selectedStyleId: preset.id, // ulož ID vybraného stylu
      colors, // Načtené barvy z Supabase
      colorPresetId, // ID presetu z Supabase
      customColors: undefined, // Vyčistit staré customColors
    }));
    setPosterSettings(prev => ({
      ...prev,
      styleId: preset.posterStyleId || preset.id,
    }));
  };

  // Handler pro otočení plakátu
  const handleToggleOrientation = () => {
    setPosterSettings(prev => ({
      ...prev,
      orientation: prev.orientation === 'portrait' ? 'landscape' : 'portrait'
    }));
  };

  // Update poster title from text data
  useEffect(() => {
    setPosterTitle(textData.title || '');
  }, [textData]);

  // Create MapConfig for e-commerce components
  const config: MapConfig = useMemo(() => ({
    mapIcons: mapIcons, // SVG ikony na geografických souřadnicích
    mapConfig: {
      center: [mapSettings.centerLat, mapSettings.centerLng],
      zoom: mapSettings.zoom,
      style: mapSettings.mapLayer,
    },
    mapSettings: {
      colors: mapSettings.colors,
      colorPresetId: mapSettings.colorPresetId,
      mapDetailMultiplier: mapSettings.mapDetailMultiplier,
    },
    design: {
      size: '50x70', // B2 size
      frame: posterSettings.frame,
      frameColor: posterSettings.frameColor,
      orientation: posterSettings.orientation,
      styleId: posterSettings.styleId,
    },
    text: {
      title: posterTitle,
      subtitle: textData.subtitle || '',
      coordinates: textData.detail || '',
    },
  }), [mapIcons, mapSettings, posterSettings, posterTitle, textData]);

  // Načíst draft při startu (musí být před načtením barev)
  useEffect(() => {
    // Zajistit, aby se draft načítal pouze jednou
    if (draftLoadedRef.current) {
      return;
    }

    // Označit, že draft se právě načítá (zabrání opakovanému načtení)
    draftLoadedRef.current = true;

    let cancelled = false;

    const loadDraft = async () => {
      try {
        const { loadDraftConfig } = await import('./lib/storage');
        const draft = await loadDraftConfig();
        if (cancelled) return;

        if (draft) {
          // Debug: zkontrolovat, zda draft obsahuje barvy ikon
          if (import.meta.env.DEV && draft.mapIcons) {
            /*
            draft.mapIcons.forEach(icon => {
              console.log(`[Draft Load] Icon ${icon.id}: color=${icon.color}, iconId=${icon.iconId}`);
            });
            */
          }

          // Obnovit mapIcons - zachovat barvy ikon z draftu
          if (draft.mapIcons && draft.mapIcons.length > 0) {
            setMapIcons(draft.mapIcons);
          }

          // Obnovit mapSettings - včetně colorScheme, aby se nespustil useEffect pro načtení barev
          if (draft.mapConfig) {
            const [centerLat, centerLng] = draft.mapConfig.center;
            const style = draft.mapConfig.style as any;
            let newMapLayer: any = style;

            // Legacy mapping
            if (style === 'fullmoon') {
              newMapLayer = 'dark';
            } else if (style === 'inverse') {
              newMapLayer = 'dark';
            }

            // Default valid check
            if (!['standard', 'no-labels', 'terrain', 'dark', 'minimal'].includes(newMapLayer)) {
              newMapLayer = 'no-labels';
            }

            // Zkontrolovat, zda draft obsahuje barvy
            const hasColors = !!(draft.mapSettings?.colors || draft.mapSettings?.colorPresetId);
            draftHasColorsRef.current = hasColors;

            setMapSettings(prev => ({
              ...prev,
              centerLat,
              centerLng,
              zoom: draft.mapConfig.zoom,
              mapLayer: newMapLayer,
              // Pokud máme barvy z draftu, použít je (zabrání načtení z Supabase)
              colors: draft.mapSettings?.colors || prev.colors,
              colorPresetId: draft.mapSettings?.colorPresetId || prev.colorPresetId,
              mapDetailMultiplier: draft.mapSettings?.mapDetailMultiplier || prev.mapDetailMultiplier,
              // Pokud draft obsahuje colorScheme, použít ho (ale musíme ho odvodit z mapLayer)
              // colorScheme zůstává z předchozího stavu, pokud není v draftu
            }));
          }

          // Obnovit posterSettings
          if (draft.design) {
            setPosterSettings(prev => ({
              ...prev,
              frame: draft.design.frame,
              frameColor: draft.design.frameColor || prev.frameColor,
              orientation: draft.design.orientation,
              styleId: draft.design.styleId || prev.styleId,
              // size zůstává 'B2' (design.size je '50x70' což je ekvivalent)
            }));
          }

          // Obnovit textData
          if (draft.text) {
            setTextData(prev => ({
              ...prev,
              title: draft.text.title || prev.title,
              subtitle: draft.text.subtitle || prev.subtitle,
              detail: draft.text.coordinates || prev.detail,
            }));
          }

          // console.log('📦 Draft configuration restored');
        } else {
          // Pokud nebyl žádný draft, označit, že barvy nejsou z draftu
          draftHasColorsRef.current = false;
        }
      } catch (error) {
        console.error('Failed to load draft:', error);
        draftHasColorsRef.current = false;
      }
    };

    loadDraft();

    return () => {
      cancelled = true;
    };
  }, []);

  // Auto-save draft (debounced)
  useEffect(() => {
    // Neukládat draft, pokud se právě načítá
    if (!draftLoadedRef.current) {
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      if (cancelled) return;

      try {
        // Debug: zkontrolovat, zda config obsahuje barvy ikon
        if (import.meta.env.DEV && config.mapIcons) {
          /*
          config.mapIcons.forEach(icon => {
            console.log(`[Draft Save] Icon ${icon.id}: color=${icon.color}, iconId=${icon.iconId}`);
          });
          */
        }
        const { saveDraftConfig } = await import('./lib/storage');
        await saveDraftConfig(config);
      } catch (error) {
        console.error('Failed to save draft:', error);
      }
    }, 2000); // Uložit po 2s nečinnosti

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [config]);

  const generateId = useCallback(() => `element-${Date.now()}-${Math.random()}`, []);

  // Legacy functions removed:
  // - handleAddIcon: no longer used (legacy icon system)
  // - addElement: was only used by handleAddIcon

  const handleAddMapIcon = useCallback((icon: MapIcon) => {
    setMapIcons((prev) => [...prev, icon]);
    setSelectedIconId(null); // Reset selection after adding
  }, []);

  const handleMapClick = useCallback(async (lat: number, lng: number) => {
    const currentSelectedIconId = selectedIconIdRef.current;
    // If an icon is selected, add it to the map at clicked coordinates
    if (currentSelectedIconId) {
      // Load icon data to get default dimensions
      const iconResult = await getMapIcon(currentSelectedIconId);
      const defaultWidth = iconResult.success && iconResult.data ? iconResult.data.default_width : 40;
      const defaultHeight = iconResult.success && iconResult.data ? iconResult.data.default_height : 40;

      // Normalizovat barvu před uložením - zajistit, že je vždy validní hex barva
      let normalizedColor = normalizeHexColor(selectedIconColor);
      // Pokud normalizace selže, zkusit použít selectedIconColor přímo (může být už validní)
      if (!normalizedColor) {
        // Zkontrolovat, jestli selectedIconColor je už validní hex barva
        if (validateHexColor(selectedIconColor)) {
          normalizedColor = selectedIconColor;
        } else {
          // Pokud ani to není validní, použít defaultní černou
          normalizedColor = '#000000';
        }
      }

      // if (import.meta.env.DEV) {
      //   console.log('[App] handleMapClick: selectedIconColor=', selectedIconColor, 'normalizedColor=', normalizedColor, 'icon.color will be:', normalizedColor);
      // }

      const newIcon: MapIcon = {
        id: generateId(),
        iconId: currentSelectedIconId,
        lat,
        lng,
        width: defaultWidth,
        height: defaultHeight,
        // Použít normalizovanou barvu z color pickeru v panelu Icons
        color: normalizedColor,
        zIndex: nextZIndex,
      };
      handleAddMapIcon(newIcon);
      setNextZIndex((prev) => prev + 1);
    } else {
      // If no icon is selected, deselect any selected map icon
      setSelectedMapIconId(null);
    }
  }, [nextZIndex, handleAddMapIcon, generateId, selectedIconColor]);

  const handleIconClick = useCallback((iconId: string) => {
    setSelectedMapIconId(iconId);
    setSelectedIconId(null); // Deselect icon selector when clicking on map icon
    // Automaticky přepnout na kategorii 'icons' na desktopu
    if (!isMobile) {
      setSelectedCategory('icons');
    }
    // On mobile, open bottom sheet for icon editing
    if (isMobile) {
      setIsIconEditorBottomSheetOpen(true);
    }
  }, [isMobile]);

  const handleUpdateMapIcon = (id: string, updates: Partial<MapIcon>) => {
    // Validovat barvu pokud je v updates
    if (updates.color !== undefined) {
      // Pokud je barva prázdná, použít undefined (fallback na default)
      if (!updates.color || updates.color.trim() === '') {
        updates.color = undefined;
      } else {
        // Validovat hex formát - použít inline validaci pro jednoduchost
        const isValidHex = /^#[0-9A-F]{6}$/i.test(updates.color) || /^#[0-9A-F]{3}$/i.test(updates.color);
        if (!isValidHex) {
          // Pokud není validní, použít undefined
          updates.color = undefined;
        }
      }
    }

    setMapIcons((prev) => {
      const updated = prev.map((icon) => (icon.id === id ? { ...icon, ...updates } : icon));
      // Debug: zkontrolovat, zda se barva správně ukládá
      // if (import.meta.env.DEV && updates.color !== undefined) {
      //   const updatedIcon = updated.find(i => i.id === id);
      //   console.log(`[App] Icon ${id} updated: color=${updates.color}, stored=${updatedIcon?.color}`);
      // }
      return updated;
    });
  };

  const handleIconUpdate = useCallback((iconId: string, lat: number, lng: number) => {
    setMapIcons((prev) =>
      prev.map((icon) => (icon.id === iconId ? { ...icon, lat, lng } : icon))
    );
  }, []);

  const handleDeleteMapIcon = (id: string) => {
    setMapIcons((prev) => prev.filter((icon) => icon.id !== id));
    if (selectedId === id) {
      setSelectedId(null);
    }
  };

  const handleUpdateElement = (id: string, updates: Partial<CanvasElement>) => {
    setElements((prev) =>
      prev.map((el) => (el.id === id ? { ...el, ...updates } as CanvasElement : el))
    );
  };

  // const handleDeleteElement = (id: string) => {
  //   setElements((prev) => prev.filter((el) => el.id !== id));
  //   if (selectedId === id) {
  //     setSelectedId(null);
  //   }
  // };

  const handleMapSettingsChange = (updates: Partial<MapSettings>) => {
    setMapSettings((prev) => ({ ...prev, ...updates }));
  };

  const handleMapInteraction = (lat: number, lng: number, zoom: number) => {
    setMapSettings((prev) => ({
      ...prev,
      centerLat: lat,
      centerLng: lng,
      zoom: zoom,
    }));
  };

  const handlePosterSettingsChange = (updates: Partial<PosterSettings>) => {
    setPosterSettings((prev) => ({ ...prev, ...updates }));
  };

  const handleLocationSelect = (lat: number, lng: number, city: string, country?: string) => {
    setMapSettings((prev) => ({
      ...prev,
      centerLat: lat,
      centerLng: lng,
    }));

    // Update title (city) and subtitle (country) text data
    setTextData(prev => ({
      ...prev,
      title: city,
      subtitle: country || prev.subtitle, // Keep existing subtitle if country not available
      detail: formatCoordinates(lat, lng), // Update coordinates
    }));

    // Close bottom sheet on mobile to show the map with new location
    if (isMobile) {
      setIsBottomSheetOpen(false);
    }
  };

  const selectedElement = elements.find((el) => el.id === selectedId) || null;

  const getWorkspaceStyle = (): React.CSSProperties => {
    if (workspaceBackground === 'living-room') {
      return {
        backgroundImage: 'url(https://mir-s3-cdn-cf.behance.net/project_modules/fs/2e061099272483.5eef57cb4080f.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      };
    }
    if (workspaceBackground === 'gray') {
      return {
        backgroundColor: 'var(--gray-light)',
        backgroundImage: `
          radial-gradient(circle at 1px 1px, rgba(0,0,0,0.15) 1px, transparent 0)
        `,
        backgroundSize: '20px 20px',
        backgroundPosition: '0 0, 0 0',
      };
    }
    if (workspaceBackground === 'css-shadow') {
      return {
        background: 'linear-gradient(135deg, var(--white-cream) 0%, var(--white-linen) 50%, var(--beige-light) 100%)',
        minHeight: '100dvh',
      };
    }
    if (workspaceBackground === 'liana') {
      return {
        backgroundImage: 'url(/textures/liana-mikah-HstwCJX0jT4-unsplash.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      };
    }
    if (workspaceBackground === 'david') {
      return {
        backgroundImage: 'url(/textures/david-van-dijk-3LTht2nxd34-unsplash.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      };
    }
    return {
      backgroundColor: 'var(--gray-light)',
      backgroundImage: `
        radial-gradient(circle at 1px 1px, rgba(0,0,0,0.15) 1px, transparent 0)
      `,
      backgroundSize: '20px 20px',
      backgroundPosition: '0 0, 0 0',
    };
  };

  const handleCheckout = useCallback(async (price?: number) => {
    // Track AddToCart event
    if (price) {
      trackPixelEvent('AddToCart', {
        content_type: 'product',
        value: price,
        currency: 'EUR',
        content_name: 'Map Poster',
      });
    }

    setCheckoutLoading(true);

    // Dočasně zakomentováno - zobrazit overlay místo checkoutu
    setShowOpeningSoonOverlay(true);
    setCheckoutLoading(false);
    return;

    /* ZAKOMENTOVÁNO - Shopify checkout dočasně vypnutý
    try {
      // Vytvořit konfiguraci (bez preview - přeskočíme generování preview pro rychlejší checkout)
      const response = await createConfiguration(config as any);

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Nepodařilo se uložit konfiguraci');
      }

      const { configurationId, previewUrl: apiPreviewUrl } = response.data;

      // Získat variant ID pro Shopify
      // Poster má pouze varianty pro velikosti (bez frame kombinací)
      const size = posterSettings.size === 'B2' ? '50x70' : posterSettings.size;
      const frame = posterSettings.frame || 'none';
      const frameColor = (posterSettings.frameColor as 'oak' | 'white' | 'black') || 'oak';

      // Vždy získat poster variant ID pouze podle velikosti
      const posterVariantId = await getPosterVariantId(size);
      if (!posterVariantId) {
        // Soft error message for user
        const errorMessage = i18n.language === 'cs'
          ? `Omlouváme se, varianta plakátu pro velikost ${size} momentálně není dostupná. Zkuste prosím jinou velikost.`
          : `Sorry, the poster variant for size ${size} is currently unavailable. Please try a different size.`;

        alert(errorMessage);
        setCheckoutLoading(false);
        return;
      }

      let frameVariantId: string | null = null;
      let hangerVariantId: string | null = null;

      // Pokud je vybrán frame, přidáme frame jako samostatný produkt
      if (frame === 'frame') {
        // Použijeme původní size z posterSettings (B2) místo převedeného (50x70)
        const frameSize = posterSettings.size === 'B2' ? 'B2' : posterSettings.size;
        // console.log(`[handleCheckout] Getting frame variant for size: ${frameSize}, color: ${frameColor}`);
        frameVariantId = await getFrameVariantId(frameSize, frameColor);

        if (!frameVariantId) {
          console.error(`[handleCheckout] Frame variant not found for ${frameColor}-${frameSize}. Trying fallback...`);
          // Zkusit také s převedeným size jako fallback
          frameVariantId = await getFrameVariantId(size, frameColor);
          if (!frameVariantId) {
            console.error(`[handleCheckout] Frame variant not found even with fallback size ${size}. Adding poster only.`);
          } else {
            // console.log(`[handleCheckout] Frame variant found with fallback size: ${frameVariantId}`);
          }
        } else {
          // console.log(`[handleCheckout] Frame variant found: ${frameVariantId}`);
        }
      }

      // Pokud je vybrán hanger, přidáme hanger jako samostatný produkt
      if (frame === 'hanger') {
        // Použijeme původní size z posterSettings (B2) místo převedeného (50x70)
        const hangerSize = posterSettings.size === 'B2' ? 'B2' : posterSettings.size;
        // console.log(`[handleCheckout] Getting hanger variant for size: ${hangerSize}, color: ${frameColor}`);
        hangerVariantId = await getHangerVariantId(hangerSize, frameColor);

        if (!hangerVariantId) {
          console.error(`[handleCheckout] Hanger variant not found for ${frameColor}-${hangerSize}. Trying fallback...`);
          // Zkusit také s převedeným size jako fallback
          hangerVariantId = await getHangerVariantId(size, frameColor);
          if (!hangerVariantId) {
            console.error(`[handleCheckout] Hanger variant not found even with fallback size ${size}. Adding poster only.`);
          } else {
            // console.log(`[handleCheckout] Hanger variant found with fallback size: ${hangerVariantId}`);
          }
        } else {
          // console.log(`[handleCheckout] Hanger variant found: ${hangerVariantId}`);
        }
      }

      // Získat aktuální jazyk z i18n a mapovat na Shopify locale
      const currentLanguage = i18n.language || 'fi'; // fallback na finštinu
      // Shopify používá formát: cs-cs, fi-fi, en-en
      // Použijeme pouze základní kód jazyka (cs, fi, en)
      const shopifyLocale = currentLanguage.split('-')[0]; // Získat pouze základní kód (cs z cs-CZ)

      // Zjistit název color presetu nebo "Custom"
      let colorPresetName: string | undefined = undefined;
      if (mapSettings.colorPresetId) {
        // Pokud existuje colorPresetId, načíst název presetu
        try {
          const presetResult = await getColorPreset(mapSettings.colorPresetId);
          if (presetResult.success && presetResult.data) {
            colorPresetName = presetResult.data.name;
            // console.log(`[handleCheckout] Color preset found: ${colorPresetName}`);
          } else {
            console.warn(`[handleCheckout] Color preset not found for ID: ${mapSettings.colorPresetId}`);
          }
        } catch (error) {
          console.warn('[handleCheckout] Failed to load color preset:', error);
        }
      } else if (mapSettings.colors) {
        // Pokud existují custom barvy bez colorPresetId, použít "Custom"
        colorPresetName = 'Custom';
        // console.log('[handleCheckout] Using custom colors');
      }

      // Vytvořit Shopify cart a přesměrovat
      // console.log(`[handleCheckout] Creating cart with poster: ${posterVariantId}, frame: ${frameVariantId || 'none'}, hanger: ${hangerVariantId || 'none'}, locale: ${shopifyLocale}, colorPreset: ${colorPresetName || 'none'}`);
      const checkoutUrl = await createShopifyCart({
        posterVariantId,
        frameVariantId: frameVariantId || undefined,
        hangerVariantId: hangerVariantId || undefined,
        trackingId: configurationId,
        previewUrl: apiPreviewUrl || undefined, // Použít preview URL z API response, pokud je dostupné
        mapCenter: posterTitle || 'Custom Location',
        size,
        frame: frame === 'hanger' || frame === 'frame' ? 'none' : frame,
        frameColor,
        locale: shopifyLocale, // Přidat locale (cs, fi, nebo en)
        textData: {
          title: textData.title,
          subtitle: textData.subtitle,
          detail: textData.detail,
        },
        colorPresetName,
      });

      // Smazat draft po úspěšném checkoutu
      await clearDraftConfig();

      // Přesměrovat na Shopify checkout
      window.location.href = checkoutUrl;
    } catch (error) {
      console.error('Checkout error:', error);
      alert(error instanceof Error ? error.message : 'Něco se pokazilo při vytváření košíku');
      setCheckoutLoading(false);
    }
    */
  }, [config, posterSettings, posterTitle, i18n.language]);

  // Reset checkout loading state when page is shown (e.g. user navigates back from Shopify)
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      // If persisted is true, the page was restored from BFCache
      if (event.persisted) {
        // console.log('[App] Page restored from BFCache, resetting checkout loading state');
        setCheckoutLoading(false);
      }
    };

    window.addEventListener('pageshow', handlePageShow);

    // Also reset on visibility change just in case
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && checkoutLoading) {
        // console.log('[App] Page became visible, resetting checkout loading state');
        setCheckoutLoading(false);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkoutLoading]);

  // Přidat do košíku bez přesměrování
  const handleAddToCart = useCallback(async () => {
    setCheckoutLoading(true);

    // Dočasně zakomentováno - zobrazit overlay místo checkoutu
    setShowOpeningSoonOverlay(true);
    setCheckoutLoading(false);
    return;

    /* ZAKOMENTOVÁNO - Shopify checkout dočasně vypnutý
    try {
      // Vytvořit konfiguraci (bez preview - přeskočíme generování preview pro rychlejší checkout)
      const response = await createConfiguration(config as any);

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Nepodařilo se uložit konfiguraci');
      }

      const { configurationId, previewUrl: apiPreviewUrl } = response.data;

      // Sanitize settings to prevent race conditions (e.g. A2 + Frame)
      let { size: rawSize, frame: rawFrame, frameColor: rawFrameColor } = posterSettings;

      // Force A2 to not have 'frame' (wooden frame) as it's not supported
      if (rawSize === 'A2' && rawFrame === 'frame') {
        console.warn('[handleCheckout] Detected invalid A2+Frame combination, auto-correcting to none');
        rawFrame = 'none';
      }

      // Získat variant ID pro Shopify
      const size = rawSize === 'B2' ? '50x70' : rawSize;
      const frame = rawFrame || 'none';
      const frameColor = (rawFrameColor as 'oak' | 'white' | 'black') || 'pine';

      // Vždy získat poster variant ID pouze podle velikosti
      const posterVariantId = await getPosterVariantId(size);
      if (!posterVariantId) {
        throw new Error(`Poster variant ID not found for size ${size}. Please try again.`);
      }

      let frameVariantId: string | null = null;
      let hangerVariantId: string | null = null;

      // Pokud je vybrán frame, přidáme frame jako samostatný produkt
      if (frame === 'frame') {
        const frameSize = posterSettings.size === 'B2' ? 'B2' : posterSettings.size;
        // console.log(`[handleAddToCart] Getting frame variant for size: ${frameSize}, color: ${frameColor}`);
        frameVariantId = await getFrameVariantId(frameSize, frameColor);

        if (!frameVariantId) {
          console.warn(`[handleAddToCart] Frame variant not found for ${frameColor}-${frameSize}. Trying fallback...`);
          frameVariantId = await getFrameVariantId(size, frameColor);
          if (!frameVariantId) {
            console.error(`[handleAddToCart] Frame variant not found even with fallback size ${size}. Frame will NOT be added to cart.`);
          } else {
            // console.log(`[handleAddToCart] Frame variant found with fallback size: ${frameVariantId}`);
          }
        } else {
          // console.log(`[handleAddToCart] Frame variant found: ${frameVariantId}`);
        }
      }

      // Pokud je vybrán hanger, přidáme hanger jako samostatný produkt
      if (frame === 'hanger') {
        const hangerSize = posterSettings.size === 'B2' ? 'B2' : posterSettings.size;
        // console.log(`[handleAddToCart] Getting hanger variant for size: ${hangerSize}, color: ${frameColor}`);
        hangerVariantId = await getHangerVariantId(hangerSize, frameColor);

        if (!hangerVariantId) {
          console.warn(`[handleAddToCart] Hanger variant not found for ${frameColor}-${hangerSize}. Trying fallback...`);
          hangerVariantId = await getHangerVariantId(size, frameColor);
          if (!hangerVariantId) {
            console.error(`[handleAddToCart] Hanger variant not found even with fallback size ${size}. Hanger will NOT be added to cart.`);
          } else {
            // console.log(`[handleAddToCart] Hanger variant found with fallback size: ${hangerVariantId}`);
          }
        } else {
          // console.log(`[handleAddToCart] Hanger variant found: ${hangerVariantId}`);
        }
      }

      // Získat aktuální jazyk z i18n
      const currentLanguage = i18n.language || 'fi';
      const shopifyLocale = currentLanguage.split('-')[0];

      // Zjistit název color presetu nebo "Custom"
      let colorPresetName: string | undefined = undefined;
      if (mapSettings.colorPresetId) {
        try {
          const presetResult = await getColorPreset(mapSettings.colorPresetId);
          if (presetResult.success && presetResult.data) {
            colorPresetName = presetResult.data.name;
          }
        } catch (error) {
          console.warn('[handleAddToCart] Failed to load color preset:', error);
        }
      } else if (mapSettings.colors) {
        colorPresetName = 'Custom';
      }

      // Vytvořit Shopify cart (ale nepřesměrovat)
      // console.log(`[handleAddToCart] Creating cart with poster: ${posterVariantId}, frame: ${frameVariantId || 'none'}, hanger: ${hangerVariantId || 'none'}, locale: ${shopifyLocale}, colorPreset: ${colorPresetName || 'none'}`);
      await createShopifyCart({
        posterVariantId,
        frameVariantId: frameVariantId || undefined,
        hangerVariantId: hangerVariantId || undefined,
        trackingId: configurationId,
        previewUrl: apiPreviewUrl || undefined,
        mapCenter: posterTitle || 'Custom Location',
        size,
        frame: frame === 'hanger' || frame === 'frame' ? 'none' : frame,
        frameColor,
        locale: shopifyLocale,
        textData: {
          title: textData.title,
          subtitle: textData.subtitle,
          detail: textData.detail,
        },
        colorPresetName,
      });

      // Smazat draft po úspěšném přidání do košíku
      await clearDraftConfig();

      // Zobrazit potvrzení (volitelné)
      // console.log('[handleAddToCart] Item added to cart successfully');

      setCheckoutLoading(false);
    } catch (error) {
      console.error('Add to cart error:', error);
      alert(error instanceof Error ? error.message : 'Něco se pokazilo při přidávání do košíku');
      setCheckoutLoading(false);
    }
    */
  }, [config, posterSettings, posterTitle, i18n.language, mapSettings, textData]);

  // Resetovat mapu na defaultní hodnoty a smazat draft
  const handleClearMap = useCallback(async () => {
    // Smazat draft
    const { clearDraftConfig } = await import('./lib/storage');
    await clearDraftConfig();
    draftLoadedRef.current = false;
    draftHasColorsRef.current = false;

    // Resetovat všechny stavy na defaultní hodnoty
    setMapIcons([]);
    setElements([]);
    setSelectedIconId(null);
    setSelectedMapIconId(null);
    setSelectedId(null);
    setNextZIndex(1);

    // Resetovat na "simple blue" preset
    const resetToSimpleBlue = async () => {
      try {
        const presetResult = await loadSimpleBluePreset();

        setMapSettings({
          centerLat: 61.4978, // Tampere, Finland
          centerLng: 23.7610,
          zoom: 11.7,
          colorScheme: 'monochrome', // Zůstává pro kompatibilitu
          mapLayer: 'no-labels',
          showLabels: false,
          showBuildings: true,
          customColors: undefined,
          selectedStyleId: DEFAULT_POSTER_STYLE_ID,
          detailLevel: 'standard',
          showMinorRoads: true,
          showPaths: true,
          showPedestrian: true,
          mapDetailMultiplier: isMobile ? 2 : 4,
          colors: presetResult.success && presetResult.data ? presetResult.data.colors : undefined,
          colorPresetId: presetResult.success && presetResult.data ? presetResult.data.id : undefined,
        });
      } catch (error) {
        console.error('Failed to load simple blue preset on reset:', error);
        // Fallback na základní nastavení
        setMapSettings({
          centerLat: 61.4978,
          centerLng: 23.7610,
          zoom: 11.7,
          colorScheme: 'monochrome',
          mapLayer: 'no-labels',
          showLabels: false,
          showBuildings: true,
          customColors: undefined,
          selectedStyleId: DEFAULT_POSTER_STYLE_ID,
          detailLevel: 'standard',
          showMinorRoads: true,
          showPaths: true,
          showPedestrian: true,
          mapDetailMultiplier: isMobile ? 2 : 4,
          colors: undefined,
          colorPresetId: undefined,
        });
      }
    };

    resetToSimpleBlue();

    setPosterSettings({
      size: 'A2',
      orientation: 'portrait',
      frame: 'hanger',
      frameColor: 'pine',
      styleId: DEFAULT_POSTER_STYLE_ID,
    });

    setTextData({
      title: 'Tampere',
      subtitle: 'Finland',
      detail: '61.4978°N / 23.7610°E'
    });

    setPosterTitle('Tampere');
    setWorkspaceBackground('gray');
    setSelectedCategory('map');

    // console.log('🗑️ Map cleared and draft deleted');
  }, []);

  // Hash routing for legal pages
  const scrollPositionRef = useRef<number>(0);

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#/', '');
      if (hash && ['privacy', 'terms', 'company', 'accessibility', 'shipping', 'withdrawal', 'contact'].includes(hash)) {
        // Save scroll position before opening modal
        scrollPositionRef.current = window.scrollY;
        setLegalPage(hash);
        // Prevent scroll to top when hash changes
        requestAnimationFrame(() => {
          window.scrollTo({ top: scrollPositionRef.current, behavior: 'auto' });
        });
      } else {
        setLegalPage(null);
        // Restore scroll position when closing modal
        requestAnimationFrame(() => {
          window.scrollTo({ top: scrollPositionRef.current, behavior: 'auto' });
        });
      }
    };

    // Check initial hash
    handleHashChange();

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const closeLegalPage = () => {
    setLegalPage(null);
    window.location.hash = '';
    // Restore scroll position after closing
    requestAnimationFrame(() => {
      window.scrollTo({ top: scrollPositionRef.current, behavior: 'auto' });
    });
  };

  const metaDescription = `Luo laadukas kustomoitu karttajuliste paikasta ${textData.title || 'mistä tahansa'}. Korkealaatuinen painatus, useita tyylejä ja kehyksiä.`;

  const jsonLdData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": `Custom Map Poster - ${textData.title || 'Any Location'}`,
    "description": metaDescription,
    "brand": {
      "@type": "Brand",
      "name": "Custom Map Posters"
    },
    "image": "https://maps-configurator.pages.dev/og-image.jpg",
    "offers": {
      "@type": "Offer",
      "price": "39.90",
      "priceCurrency": "EUR",
      "availability": "https://schema.org/InStock"
    }
  };

  return (
    <ErrorBoundary>
      <div
        className={`min-h-screen flex flex-col ${workspaceBackground === 'david' ? 'workspace-bg-mirror' : ''}`}
        style={getWorkspaceStyle()}
      >
        <SEOMeta
          title={posterTitle ? `Map Poster of ${posterTitle}` : undefined}
          description={metaDescription}
        />
        <JsonLd data={jsonLdData} />
        {/* Sticky header section - AnnouncementBar + Toolbar bez mezery */}
        <div ref={headerRef} className="sticky top-0 z-50 flex flex-col">
          <AnnouncementBar />
          <header className="z-40 bg-white-cream/95 backdrop-blur-lg border-b border-wood-warm/30 shadow-sm">
            <Toolbar onClearMap={handleClearMap} />
          </header>
        </div>

        <div className="flex-1 flex flex-col lg:flex-row">
          {/* Left sidebar - sticky na desktopu, skrytý na mobilu */}
          <aside
            className="hidden lg:flex lg:w-[40%] flex-shrink-0 lg:sticky overflow-hidden"
            style={{
              top: `${headerHeight}px`,
              height: `calc(100dvh - ${headerHeight}px)`
            }}
          >
            <div className="ml-6 flex flex-col h-full">
              {/* Top row: CategoryNavigation + SettingsPanel */}
              <div className="flex flex-col lg:flex-row h-full">
                <CategoryNavigation
                  selectedCategory={selectedCategory}
                  onCategorySelect={setSelectedCategory}
                  selectedMapIconId={selectedMapIconId}
                  onCloseMapIconEditor={() => setSelectedMapIconId(null)}
                />
                <SettingsPanel
                  selectedCategory={selectedCategory}
                  mapSettings={mapSettings}
                  posterSettings={posterSettings}
                  workspaceBackground={workspaceBackground}
                  onMapSettingsChange={handleMapSettingsChange}
                  onPosterSettingsChange={handlePosterSettingsChange}
                  onWorkspaceBackgroundChange={setWorkspaceBackground}
                  onLocationSelect={handleLocationSelect}
                  onAddText={() => { }}
                  onAddMapIcon={handleAddMapIcon}
                  selectedIconId={selectedIconId}
                  onIconSelect={handleIconSelect}
                  onStyleSelect={handleStyleSelect}
                  posterStyleId={posterStyleId}
                  posterStyle={posterStyleDefinition}
                  textData={textData}
                  onTextChange={setTextData}
                  selectedMapIconId={selectedMapIconId}
                  mapIcons={mapIcons}
                  onUpdateMapIcon={handleUpdateMapIcon}
                  onDeleteMapIcon={handleDeleteMapIcon}
                  onCloseMapIconEditor={() => setSelectedMapIconId(null)}
                  selectedIconColor={selectedIconColor}
                  onIconColorChange={setSelectedIconColor}
                />
              </div>
            </div>
          </aside>

          {/* Main content area */}
          <main className="flex-1 lg:w-[60%] flex flex-col min-h-screen">
            {/* Canvas sekce */}
            <section
              id="canvas-section"
              className="min-h-[600px] lg:min-h-[80vh] flex items-center justify-center py-8 lg:py-12 relative"
            >
              <div id="workingtable" className="w-full h-full flex relative max-w-7xl mx-auto px-4">
                <Canvas
                  elements={elements}
                  mapIcons={mapIcons}
                  selectedId={selectedId}
                  selectedIconId={selectedIconId}
                  selectedMapIconId={selectedMapIconId}
                  selectedIconColor={selectedIconColor}
                  canvasWidth={canvasDimensions.width}
                  canvasHeight={canvasDimensions.height}
                  mapSettings={mapSettings}
                  posterSettings={posterSettings}
                  workspaceBackground={workspaceBackground}
                  onSelect={setSelectedId}
                  onUpdateElement={handleUpdateElement}
                  onMapChange={handleMapInteraction}
                  onMapClick={handleMapClick}
                  onIconClick={handleIconClick}
                  onIconUpdate={handleIconUpdate}
                  onIconSelectCancel={() => setSelectedIconId(null)}
                  textData={textData}
                  onToggleOrientation={handleToggleOrientation}
                />
                {/* MapIconEditor je nyní zobrazen v SettingsPanel pro desktop */}
                {!isMobile && selectedMapIconId ? null : (
                  selectedElement ? (
                    <PropertiesPanel element={selectedElement} onUpdate={handleUpdateElement} />
                  ) : null
                )}

              </div>
            </section>

            {/* Nové produktové sekce */}

            {/* Pricing sekce */}
            <PricingSection
              posterSettings={posterSettings}
              onPosterSettingsChange={handlePosterSettingsChange}
              onCheckout={handleCheckout}
              onAddToCart={handleAddToCart}
            />

            {/* Features sekce */}
            <FeaturesSection />

            {/* Social Proof - Testimonials */}
            <TestimonialsSection />

            {/* FAQ sekce */}
            <FAQSection />

            {/* Footer */}
            <Footer />
          </main>
        </div>

        {/* Sticky E-commerce card - Desktop */}
        {!isMobile && (
          <div className="fixed bottom-4 right-4 z-40 w-auto max-w-[320px]">
            <ECommerceCard
              config={config}
              disabled={false}
              loading={checkoutLoading}
              onCheckout={handleCheckout}
              posterSettings={posterSettings}
            />
          </div>
        )}

        {/* Mobile bottom navigation */}
        {isMobile && (
          <MobileBottomNav
            selectedCategory={selectedCategory}
            onCategorySelect={handleMobileCategorySelect}
            selectedMapIconId={selectedMapIconId}
            onCloseMapIconEditor={() => setSelectedMapIconId(null)}
          />
        )}

        {/* Mobile bottom sheet */}
        {isMobile && (
          <MobileBottomSheet
            isOpen={isBottomSheetOpen}
            onClose={() => setIsBottomSheetOpen(false)}
            title={getCategoryTitle(selectedCategory)}
          >
            {renderCategoryContent(
              selectedCategory,
              mapSettings,
              posterSettings,
              workspaceBackground,
              handleMapSettingsChange,
              handlePosterSettingsChange,
              setWorkspaceBackground,
              handleLocationSelect,
              handleAddMapIcon,
              selectedIconId,
              handleIconSelect,
              handleStyleSelect,
              posterStyleId,
              posterStyleDefinition,
              textData,
              setTextData,
              t,
              selectedMapIconId,
              mapIcons,
              handleUpdateMapIcon,
              handleDeleteMapIcon,
              () => {
                setSelectedMapIconId(null);
                setIsIconEditorBottomSheetOpen(false);
              },
              selectedIconColor,
              setSelectedIconColor
            )}
          </MobileBottomSheet>
        )}

        {/* Mobile bottom sheet pro editaci ikony */}
        {isMobile && (
          <MobileBottomSheet
            isOpen={isIconEditorBottomSheetOpen}
            onClose={() => {
              setIsIconEditorBottomSheetOpen(false);
              setSelectedMapIconId(null);
            }}
            title="Upravit ikonu"
          >
            <MapIconEditor
              icon={mapIcons.find((icon) => icon.id === selectedMapIconId) || null}
              onUpdate={handleUpdateMapIcon}
              onDelete={(iconId) => {
                handleDeleteMapIcon(iconId);
                setIsIconEditorBottomSheetOpen(false);
                setSelectedMapIconId(null);
              }}
              onClose={() => {
                setIsIconEditorBottomSheetOpen(false);
                setSelectedMapIconId(null);
              }}
            />
          </MobileBottomSheet>
        )}

        {/* Mobile sticky checkout button - positioned above bottom nav */}
        {isMobile && <MobileCheckoutButton
          posterSettings={posterSettings}
          checkoutLoading={checkoutLoading}
          onCheckout={handleCheckout}
          t={t}
        />}

        {/* Legal Page Modal */}
        {legalPage && ['privacy', 'terms', 'company', 'accessibility', 'shipping', 'withdrawal', 'contact'].includes(legalPage) && (
          <LegalPage
            type={legalPage as 'privacy' | 'terms' | 'company' | 'accessibility' | 'shipping' | 'withdrawal' | 'contact'}
            onClose={closeLegalPage}
          />
        )}

        {/* Opening Soon Overlay - zobrazí se při pokusu o nákup */}
        {showOpeningSoonOverlay && (
          <OpeningSoonOverlay onClose={() => setShowOpeningSoonOverlay(false)} />
        )}

      </div>
    </ErrorBoundary>
  );
}

export default App;
